import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Goal } from '@feathr/blackbox';
import { Checkbox, ConfirmModal, toast } from '@feathr/components';
import { errorMessage, logError } from '@feathr/hooks';

interface IDeleteGoalModalProps {
  goal: Goal;
  onClose: () => void;
  /** Called after a successful delete, e.g. to navigate away from a detail page. */
  onSuccess?: () => void;
  opened: boolean;
}

function DeleteGoalModal({
  goal,
  onClose,
  onSuccess,
  opened,
}: Readonly<IDeleteGoalModalProps>): JSX.Element {
  const { t } = useTranslation();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const name = goal.get('name');

  function handleClose(): void {
    setIsConfirmed(false);
    onClose();
  }

  function handleConfirmChange(newValue?: boolean): void {
    setIsConfirmed(!!newValue);
  }

  async function destroy(): Promise<void> {
    try {
      // TODO: method is not allowed - change when BE work completed
      await goal.delete();
      // rachis's delete sets isErrored/error rather than rejecting
      if (goal.isErrored) {
        throw goal.error;
      }
      toast(t("Deleted '{{- name}}'.", { name }), { type: 'success' });
      handleClose();
      goal.collection?.refreshApiCache();
      onSuccess?.();
    } catch (error) {
      logError({
        extra: { goalId: goal.id, message: errorMessage(error, t) },
        level: 'error',
        message: 'Failed to delete goal',
      });
      toast(t("Couldn't delete goal. Try again."), { type: 'error' });
    }
  }

  return (
    <ConfirmModal
      confirmButtonText={t('Delete')}
      confirmButtonType={'danger'}
      confirmDisabled={!isConfirmed}
      description={t(
        'This removes the goal from all campaigns and flights. No conversions have been logged against it, so nothing is lost.',
      )}
      onClose={handleClose}
      onConfirm={destroy}
      opened={opened}
      t={t}
      title={t("Delete '{{- name}}'?", { name })}
    >
      <Checkbox
        label={t('Yes, remove this goal from all campaigns and flights.')}
        name={'confirm-delete-goal'}
        onChange={handleConfirmChange}
        value={isConfirmed}
      />
    </ConfirmModal>
  );
}

export default DeleteGoalModal;
